import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TripSheetModal from './TripSheetModal';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    x: ['M18 6 6 18', 'M6 6l12 12'],
    arrowLeft: ['M19 12H5', 'M12 19l-7-7 7-7'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'M18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
    chevronDown: 'M6 9l6 6 6-6',
    chevronUp: 'M18 15l-6-6-6 6'
};

export default function TripHistory({ tripId, onBack, user }) {
    const [trip, setTrip] = useState(null);
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedInstance, setExpandedInstance] = useState(null);
    const [tripSheetModal, setTripSheetModal] = useState(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
    }, [tripId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tripRes, instRes] = await Promise.all([
                axios.get(`${API}/trips/${operatorId}`), // We'll find our trip here
                axios.get(`${API}/trip-instances/${operatorId}`)
            ]);
            
            const foundTrip = tripRes.data.find(t => t._id === tripId);
            setTrip(foundTrip);
            
            const tripInstances = instRes.data
                .filter(inst => (inst.masterTripId?._id || inst.masterTripId) === tripId)
                .sort((a, b) => new Date(a.tripDate) - new Date(b.tripDate));
            
            setInstances(tripInstances);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading history...</div>;
    if (!trip) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Trip not found.</div>;

    return (
        <div style={{ padding: '20px 40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 30 }}>
                <button 
                    onClick={onBack}
                    style={{ 
                        width: 40, height: 40, borderRadius: 12, border: '1px solid #e2e8f0', 
                        background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        cursor: 'pointer', color: '#64748b' 
                    }}
                >
                    <Icon d={icons.arrowLeft} size={20} />
                </button>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#00B2A9', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Trip ID: {String(trip._id).slice(-6).toUpperCase()}</div>
                    <h1 style={{ fontSize: 24, fontWeight: 900, color: '#1e293b', margin: 0 }}>Execution History</h1>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontWeight: 600 }}>{trip.clientId?.companyName} • {trip.routeId?.name} • {trip.tripFrequency}</p>
                </div>
            </div>

            <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f8fafc' }}>
                        <tr>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Date</th>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Vehicle & Driver</th>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                            <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Passengers</th>
                            <th style={{ width: 60 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {instances.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                                    No historical executions found for this trip.
                                </td>
                            </tr>
                        ) : (
                            instances.map(inst => (
                                <React.Fragment key={inst._id}>
                                    <tr 
                                        onClick={() => setExpandedInstance(expandedInstance === inst._id ? null : inst._id)}
                                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.2s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#fcfdfe'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <td style={{ padding: '16px 24px', fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                                            {new Date(inst.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                                    <Icon d={icons.truck} size={14} />
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{inst.vehicleId?.plateNumber || inst.vehicleId?.registrationNumber || '—'}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{inst.driverId?.name || '—'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <span className={`md-badge md-badge-${inst.status?.toLowerCase()}`}>{inst.status}</span>
                                            {inst.status === 'Completed' && inst.completedAt && (
                                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontWeight: 700 }}>
                                                    at {new Date(inst.completedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e6f0fa', color: '#11427D', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                                                    {inst.passengers?.length || 0}
                                                </div>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Passengers</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTripSheetModal({ trip, instance: inst });
                                                }}
                                                style={{ 
                                                    padding: '6px 12px', background: '#10b981', color: 'white', 
                                                    border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, 
                                                    cursor: 'pointer', marginRight: 12
                                                }}
                                            >
                                                Trip Sheet
                                            </button>
                                            <Icon d={expandedInstance === inst._id ? icons.chevronUp : icons.chevronDown} size={16} color="#94a3b8" />
                                        </td>
                                    </tr>
                                    {expandedInstance === inst._id && (
                                        <tr>
                                            <td colSpan="5" style={{ background: '#f8fafc', padding: '0 24px 24px' }}>
                                                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', marginTop: -8, overflow: 'hidden' }}>
                                                    <div style={{ padding: '12px 16px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>
                                                        Passenger Manifest for {new Date(inst.tripDate).toLocaleDateString('en-GB')}
                                                    </div>
                                                    <div style={{ padding: 16 }}>
                                                        {(!inst.passengers || inst.passengers.length === 0) ? (
                                                            <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No passengers recorded for this instance.</div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                                                                {inst.passengers.map(p => (
                                                                    <div key={p.passengerId?._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #f1f5f9' }}>
                                                                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: p.passengerId?.gender === 'Female' ? '#fdf2f8' : '#eff6ff', color: p.passengerId?.gender === 'Female' ? '#db2777' : '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                            <Icon d={icons.user} size={14} />
                                                                        </div>
                                                                        <div>
                                                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{p.passengerId?.name || 'Unknown'}</div>
                                                                            <div style={{ fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                                                                                <span style={{ color: '#64748b' }}>{p.passengerId?.employeeId || 'No ID'}</span>
                                                                                <span style={{ color: '#94a3b8' }}>•</span>
                                                                                <span style={{
                                                                                    padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 800,
                                                                                    background: p.attendanceStatus === 'Boarded' ? '#dcfce7' : p.attendanceStatus === 'Absent' ? '#fee2e2' : p.attendanceStatus === 'No Show' ? '#fef3c7' : '#dbeafe',
                                                                                    color: p.attendanceStatus === 'Boarded' ? '#16a34a' : p.attendanceStatus === 'Absent' ? '#dc2626' : p.attendanceStatus === 'No Show' ? '#d97706' : '#2563eb'
                                                                                }}>{p.attendanceStatus}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {trip.history && trip.history.length > 0 && (
                <div style={{ marginTop: 40, background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                    <div style={{ padding: '20px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', margin: 0 }}>Edit History & Audit Log</h2>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Tracks manual changes and updates made to this trip schedule.</p>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f8fafc' }}>
                            <tr>
                                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Date & Time</th>
                                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Updated By</th>
                                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Reason for Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...trip.history].sort((a,b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((h, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '16px 24px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                        {new Date(h.updatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                    <td style={{ padding: '16px 24px', fontSize: 13, color: '#475569', fontWeight: 600 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e6f0fa', color: '#11427D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Icon d={icons.user} size={12} />
                                            </div>
                                            {h.updatedBy || 'Unknown User'}
                                        </div>
                                    </td>
                                    <td style={{ padding: '16px 24px', fontSize: 13, color: '#475569' }}>
                                        <div style={{ background: '#fff7ed', border: '1px solid #ffedd5', padding: '8px 12px', borderRadius: 8, color: '#9a3412', fontWeight: 600 }}>
                                            {h.changeReason}
                                        </div>
                                        {h.changes && h.changes.length > 0 && (
                                            <div style={{ marginTop: 12, background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>Fields Changed:</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {h.changes.map((c, ci) => (
                                                        <div key={ci} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: 12 }}>
                                                            <span style={{ color: '#334155', fontWeight: 700, minWidth: '100px' }}>{c.field}:</span>
                                                            <span style={{ color: '#ef4444', textDecoration: 'line-through', padding: '2px 6px', background: '#fef2f2', borderRadius: '4px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(c.oldValue || 'none')}>
                                                                {String(c.oldValue || 'none')}
                                                            </span>
                                                            <Icon d={icons.arrowLeft} size={14} color="#94a3b8" style={{ transform: 'rotate(180deg)' }} />
                                                            <span style={{ color: '#10b981', fontWeight: 600, padding: '2px 6px', background: '#ecfdf5', borderRadius: '4px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(c.newValue || 'none')}>
                                                                {String(c.newValue || 'none')}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Trip Sheet Modal */}
            {tripSheetModal && (
                <TripSheetModal 
                    trip={tripSheetModal.trip} 
                    instance={tripSheetModal.instance} 
                    onClose={() => setTripSheetModal(null)} 
                    operatorId={operatorId} 
                />
            )}
        </div>
    );
}
